import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ChevronLeft, Save, Trash2 } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import { supabase } from '../../lib/supabase'
import Button from '../../components/ui/Button'
import Input from '../../components/ui/Input'
import ImageUpload from '../../components/admin/ImageUpload'

const categories = [
  { value: 'teethers', label: 'Teethers' },
  { value: 'gift-sets', label: 'Gift Sets' },
  { value: 'accessories', label: 'Accessories' },
]

export default function AdminProductForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { isAuthenticated, isAdmin } = useAuth()
  const isEditing = Boolean(id)

  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    category: 'teethers',
    price: '',
    compare_price: '',
    description: '',
    short_description: '',
    age_range: '',
    stock_quantity: 0,
    in_stock: true,
    featured: false,
    bestseller: false,
    coming_soon: false,
    features: [''],
    developmental_benefits: [''],
    colors: [''],
    images: [''],
  })

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/account')
      return
    }
    if (!isAdmin) {
      navigate('/')
      return
    }
    if (isEditing) {
      fetchProduct()
    }
  }, [isAuthenticated, isAdmin, id])

  const fetchProduct = async () => {
    setIsLoading(true)
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('id', id)
        .single()

      if (error) throw error

      // Debug: log what's coming from database
      console.log('Fetched product data:', data)
      console.log('Images from DB:', data.images)

      setFormData({
        ...data,
        price: data.price.toString(),
        compare_price: data.compare_price?.toString() || '',
        features: data.features?.length ? data.features : [''],
        developmental_benefits: data.developmental_benefits?.length
          ? data.developmental_benefits
          : [''],
        colors: data.colors?.length ? data.colors : [''],
        images: data.images?.length ? data.images : [''],
      })
    } catch (error) {
      console.error('Error fetching product:', error)
      navigate('/admin/products')
    } finally {
      setIsLoading(false)
    }
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    })

    // Auto-generate slug from name
    if (name === 'name') {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '')
      setFormData((prev) => ({ ...prev, slug }))
    }
  }

  const handleArrayChange = (field, index, value) => {
    const newArray = [...formData[field]]
    newArray[index] = value
    setFormData({ ...formData, [field]: newArray })
  }

  const addArrayItem = (field) => {
    setFormData({ ...formData, [field]: [...formData[field], ''] })
  }

  const removeArrayItem = (field, index) => {
    const newArray = formData[field].filter((_, i) => i !== index)
    setFormData({ ...formData, [field]: newArray.length ? newArray : [''] })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSaving(true)

    // Filter out empty strings from images array
    const filteredImages = formData.images.filter((i) => i && i.trim())
    console.log('Saving images:', filteredImages)

    try {
      const productData = {
        name: formData.name,
        slug: formData.slug,
        category: formData.category,
        price: parseFloat(formData.price),
        compare_price: formData.compare_price
          ? parseFloat(formData.compare_price)
          : null,
        description: formData.description,
        short_description: formData.short_description,
        age_range: formData.age_range,
        stock_quantity: parseInt(formData.stock_quantity),
        in_stock: formData.stock_quantity > 0,
        featured: formData.featured,
        bestseller: formData.bestseller,
        coming_soon: formData.coming_soon,
        features: formData.features.filter((f) => f && f.trim()),
        developmental_benefits: formData.developmental_benefits.filter(
          (b) => b && b.trim()
        ),
        colors: formData.colors.filter((c) => c && c.trim()),
        images: filteredImages,
      }

      console.log('Product data being saved:', productData)

      if (isEditing) {
        const { data, error } = await supabase
          .from('products')
          .update(productData)
          .eq('id', id)
          .select()

        console.log('Update response:', { data, error })
        if (error) throw error
      } else {
        const { data, error } = await supabase
          .from('products')
          .insert(productData)
          .select()

        console.log('Insert response:', { data, error })
        if (error) throw error
      }

      navigate('/admin/products')
    } catch (error) {
      console.error('Error saving product:', error)
      alert('Failed to save product: ' + error.message)
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-pp-ivory flex items-center justify-center">
        <div className="animate-pulse text-pp-stone">Loading product...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-pp-linen">
      <div className="container mx-auto px-4 lg:px-8 py-8">
        <Link
          to="/admin/products"
          className="inline-flex items-center gap-2 text-pp-stone hover:text-pp-charcoal transition-colors mb-6"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to Products
        </Link>

        <h1 className="font-display text-display-md text-pp-ink mb-8">
          {isEditing ? 'Edit Product' : 'Add New Product'}
        </h1>

        <form onSubmit={handleSubmit}>
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Form */}
            <div className="lg:col-span-2 space-y-6">
              {/* Basic Info */}
              <div className="bg-white rounded-2xl p-6 border border-pp-sand/50">
                <h2 className="font-display text-lg text-pp-ink mb-6">
                  Basic Information
                </h2>
                <div className="space-y-4">
                  <Input
                    label="Product Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                  <Input
                    label="URL Slug"
                    name="slug"
                    value={formData.slug}
                    onChange={handleChange}
                    required
                  />
                  <div>
                    <label className="block text-sm font-medium text-pp-charcoal mb-2">
                      Category
                    </label>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-pp-sand rounded-lg bg-white text-pp-ink focus:outline-none focus:ring-2 focus:ring-pp-terracotta/20"
                    >
                      {categories.map((cat) => (
                        <option key={cat.value} value={cat.value}>
                          {cat.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <Input
                    label="Age Range"
                    name="age_range"
                    value={formData.age_range}
                    onChange={handleChange}
                    placeholder="e.g., 3+ months"
                  />
                </div>
              </div>

              {/* Description */}
              <div className="bg-white rounded-2xl p-6 border border-pp-sand/50">
                <h2 className="font-display text-lg text-pp-ink mb-6">
                  Description
                </h2>
                <div className="space-y-4">
                  <Input
                    label="Short Description"
                    name="short_description"
                    value={formData.short_description}
                    onChange={handleChange}
                    placeholder="Brief tagline for product cards"
                  />
                  <div>
                    <label className="block text-sm font-medium text-pp-charcoal mb-2">
                      Full Description
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      rows={4}
                      className="w-full px-4 py-3 border border-pp-sand rounded-lg bg-white text-pp-ink focus:outline-none focus:ring-2 focus:ring-pp-terracotta/20 resize-none"
                    />
                  </div>
                </div>
              </div>

              {/* Features */}
              <div className="bg-white rounded-2xl p-6 border border-pp-sand/50">
                <h2 className="font-display text-lg text-pp-ink mb-6">
                  Features
                </h2>
                <div className="space-y-3">
                  {formData.features.map((feature, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={feature}
                        onChange={(e) =>
                          handleArrayChange('features', index, e.target.value)
                        }
                        placeholder="Enter feature"
                        className="flex-1 px-4 py-2 border border-pp-sand rounded-lg text-sm"
                      />
                      <button
                        type="button"
                        onClick={() => removeArrayItem('features', index)}
                        className="p-2 text-pp-stone hover:text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addArrayItem('features')}
                    className="text-sm text-pp-terracotta hover:underline"
                  >
                    + Add feature
                  </button>
                </div>
              </div>

              {/* Developmental Benefits */}
              <div className="bg-white rounded-2xl p-6 border border-pp-sand/50">
                <h2 className="font-display text-lg text-pp-ink mb-6">
                  Developmental Benefits
                </h2>
                <div className="space-y-3">
                  {formData.developmental_benefits.map((benefit, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={benefit}
                        onChange={(e) =>
                          handleArrayChange(
                            'developmental_benefits',
                            index,
                            e.target.value
                          )
                        }
                        placeholder="Enter benefit"
                        className="flex-1 px-4 py-2 border border-pp-sand rounded-lg text-sm"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          removeArrayItem('developmental_benefits', index)
                        }
                        className="p-2 text-pp-stone hover:text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addArrayItem('developmental_benefits')}
                    className="text-sm text-pp-terracotta hover:underline"
                  >
                    + Add benefit
                  </button>
                </div>
              </div>

              {/* Images */}
              <div className="bg-white rounded-2xl p-6 border border-pp-sand/50">
                <h2 className="font-display text-lg text-pp-ink mb-6">
                  Product Images
                </h2>
                <ImageUpload
                  images={formData.images}
                  onChange={(newImages) => setFormData((prev) => ({ ...prev, images: newImages }))}
                  maxImages={5}
                />
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Pricing */}
              <div className="bg-white rounded-2xl p-6 border border-pp-sand/50">
                <h2 className="font-display text-lg text-pp-ink mb-6">
                  Pricing
                </h2>
                <div className="space-y-4">
                  <Input
                    label="Price (AUD)"
                    type="number"
                    step="0.01"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    required
                  />
                  <Input
                    label="Compare at Price (optional)"
                    type="number"
                    step="0.01"
                    name="compare_price"
                    value={formData.compare_price}
                    onChange={handleChange}
                    placeholder="Original price for sale items"
                  />
                </div>
              </div>

              {/* Inventory */}
              <div className="bg-white rounded-2xl p-6 border border-pp-sand/50">
                <h2 className="font-display text-lg text-pp-ink mb-6">
                  Inventory
                </h2>
                <Input
                  label="Stock Quantity"
                  type="number"
                  min="0"
                  name="stock_quantity"
                  value={formData.stock_quantity}
                  onChange={handleChange}
                />
              </div>

              {/* Status */}
              <div className="bg-white rounded-2xl p-6 border border-pp-sand/50">
                <h2 className="font-display text-lg text-pp-ink mb-6">
                  Status
                </h2>
                <div className="space-y-4">
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      name="featured"
                      checked={formData.featured}
                      onChange={handleChange}
                      className="w-5 h-5 rounded border-pp-sand text-pp-terracotta focus:ring-pp-terracotta"
                    />
                    <span className="text-pp-charcoal">Featured product</span>
                  </label>
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      name="bestseller"
                      checked={formData.bestseller}
                      onChange={handleChange}
                      className="w-5 h-5 rounded border-pp-sand text-pp-terracotta focus:ring-pp-terracotta"
                    />
                    <span className="text-pp-charcoal">Bestseller</span>
                  </label>
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      name="coming_soon"
                      checked={formData.coming_soon}
                      onChange={handleChange}
                      className="w-5 h-5 rounded border-pp-sand text-pp-terracotta focus:ring-pp-terracotta"
                    />
                    <span className="text-pp-charcoal">Coming Soon</span>
                  </label>
                </div>
              </div>

              {/* Colors */}
              <div className="bg-white rounded-2xl p-6 border border-pp-sand/50">
                <h2 className="font-display text-lg text-pp-ink mb-6">
                  Colors
                </h2>
                <div className="space-y-3">
                  {formData.colors.map((color, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={color}
                        onChange={(e) =>
                          handleArrayChange('colors', index, e.target.value)
                        }
                        placeholder="Color name"
                        className="flex-1 px-4 py-2 border border-pp-sand rounded-lg text-sm"
                      />
                      <button
                        type="button"
                        onClick={() => removeArrayItem('colors', index)}
                        className="p-2 text-pp-stone hover:text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addArrayItem('colors')}
                    className="text-sm text-pp-terracotta hover:underline"
                  >
                    + Add color
                  </button>
                </div>
              </div>

              {/* Submit */}
              <Button type="submit" className="w-full" isLoading={isSaving}>
                <Save className="w-4 h-4" />
                {isEditing ? 'Update Product' : 'Create Product'}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}
